<script setup lang="ts">
const localePath = useLocalePath();
const { checkGiftCardCodeSchema } = useGiftCard();
const businessStore = useBusinessStore();
const {
  business,
  selectedLocation,
  goToCheckoutMembership,
  selectedPaymentMethod,
  tapLoading,
  isNavigating,
  isCardValid,
  membershipSummary,
  payMembershipLoading,
  invoice,
} = storeToRefs(businessStore);
const route = useRoute();
const globalStore = useGlobalStore();
const { isLoading, progress, isClientBlocked } = storeToRefs(globalStore);
const client = useClient();
const { isLoggedIn } = useAuth();
const { payMembership } = useMemberships();
const { navLoading, runWithLoading } = useFooterNav();

export type ActiveButton = "review-and-confirm" | "checkout";

const activeButton = computed((): ActiveButton | undefined => {
  if (typeof route.name === "string") {
    if (route.name.includes("review-and-confirm")) return "review-and-confirm";
    if (route.name.includes("checkout")) return "checkout";
  }
  return undefined;
});

const loginToContinue = () => {
  goToCheckoutMembership.value = true;
  globalStore.openSignInModal({
    onSuccess: (c) => {
      if (c?.profile_completed && c?.mobile) {
        runWithLoading(handleConfirm);
      }
    },
  });
};

const completeProfileToContinue = async () => {
  goToCheckoutMembership.value = true;
  await navigateTo(localePath(`/complete`));
};

const submitPayment = async () => {
  if (selectedPaymentMethod.value?.key === "tap-cc") {
    const windowCardSDK = ref(window.CardSDK);
    const { tokenize } = windowCardSDK.value;
    tapLoading.value = true;
    await tokenize();
    return;
  } else if (selectedPaymentMethod.value?.key === "gift-card") {
    const { errors } = await checkGiftCardCodeSchema();
    if (errors) {
      return;
    }
  }

  await payMembership();
};

const displayTotal = computed(() =>
  useFormatAmount(
    membershipSummary.value?.grand_total ?? membershipSummary.value?.subtotal,
  ),
);

const handleConfirm = async () => {
  if (
    membershipSummary.value!.grand_total === 0 ||
    membershipSummary.value!.grand_total === "0.000"
  ) {
    await submitPayment();
    return;
  }
  await navigateTo(
    localePath(`/${selectedLocation.value?.identifier}/memberships/checkout`)
  );
};
</script>

<template>
  <CommonFooterShell
    :label1="$t('memberships')"
    value1="1"
    :label2="$t('total')"
    :value2="$formatPrice(displayTotal, business?.currency)"
  >
    <template #action>
      <template v-if="activeButton === 'review-and-confirm'">
        <template v-if="isLoggedIn">
          <UButton
            v-if="client?.profile_completed"
            :disabled="isLoading || progress || isClientBlocked"
            :loading="navLoading || payMembershipLoading"
            :label="$t('label.confirm')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 text-center flex items-center justify-center"
            @click="runWithLoading(handleConfirm)"
          />
          <UButton
            v-else
            :disabled="isLoading || progress"
            :loading="navLoading"
            :label="$t('label.complete_profile')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center truncate"
            @click="runWithLoading(completeProfileToContinue)"
          />
        </template>
        <UButton
          v-else
          :label="$t('label.sign_in')"
          :disabled="isLoading || progress"
          :loading="navLoading"
          :color="`${selectedLocation?.theme_name}-secondary`"
          size="xl"
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(loginToContinue)"
        />
      </template>

      <UButton
        v-if="activeButton === 'checkout'"
        :label="$t('label.checkout')"
        :loading="payMembershipLoading || tapLoading"
        :color="`${selectedLocation?.theme_name}-secondary`"
        size="xl"
        :disabled="
          !selectedPaymentMethod?.id ||
          (!isCardValid && selectedPaymentMethod?.key === 'tap-cc') ||
          (!!selectedPaymentMethod?.accept_invoice && !invoice) ||
          isNavigating
        "
        class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
        @click="submitPayment"
      />
    </template>
  </CommonFooterShell>
</template>
